import { InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useEffect } from 'react';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

interface ListHospitalProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio?: string;
  dataReference?: DateTime | null;
}

const ListHospitalComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  isSmallSize = false,
  idDominio = '3',
  dataReference,
}: ListHospitalProps): React.ReactElement => {
  const [hospitals, getHospitals] = useHttpFetch<
    HttpResponse<Array<ValoreDominioVO>>,
    Array<ItemVM>
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) =>
      ((response.data.payload || []) as Array<ValoreDominioVO>).map((item) => ({
        id: item.key?.idValore,
        value: item.descrizioneBreveValore,
      })) as Array<ItemVM>,
  });

  useEffect(() => {
    const transformedFilters: OperationObject = {
      idDominio: {
        value: idDominio || '',
        operation: Operation.EQUALS,
      },
      dataRiferimento: {
        value: dataReference ? dataReference.toFormat('yyyy-MM-dd') : '',
        operation: Operation.EQUALS,
      },
    };

    const queryString = `${paginationListToQueryString({
      filters: transformedFilters,
      orders: {},
    })}`;

    getHospitals({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, [idDominio, dataReference]);

  const hospitalsData: Array<ItemVM> = (hospitals?.data || []) as Array<ItemVM>;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id="hospital-label">
        {required ? 'Ospedale *' : 'Ospedale'}
      </InputLabel>
      <PmiSelect
        labelId="hospital-label"
        id="hospital"
        name="hospital"
        value={value}
        label={required ? 'Ospedale *' : 'Ospedale'}
        onChange={onChange}
        disabled={disabled}
        error={error}
        size={isSmallSize ? 'small' : undefined}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {hospitalsData?.map((hospital: ItemVM) => (
          <MenuItem key={hospital.id} value={hospital.id}>
            {hospital.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListHospitalComponent.displayName = 'List Hospital component';

const ListHospital = memo(ListHospitalComponent);

export default ListHospital as typeof ListHospitalComponent;
